import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { LeaseFormFieldName } from '../lib/form-layout/lease-fields';

export type LeaseLayoutDocument = LayoutDocument<LeaseFormFieldName>;

export interface ResolvedLayout {
  layout: LeaseLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const leaseFormLayoutKeys = {
  all: ['lease-form-layout'] as const,
  active: (projectId: string | null) => [...leaseFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...leaseFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useLeaseFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: leaseFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/lease-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

export function useAdminActiveLeaseLayout(
  scope: 'global' | 'project',
  projectId: string | null,
) {
  return useQuery({
    queryKey: leaseFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/lease-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateLeaseLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: LeaseLayoutDocument;
  notes?: string | null;
}

export function useUpdateLeaseFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLeaseLayoutInput) =>
      api.put<ResolvedLayout>('/admin/lease-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaseFormLayoutKeys.all }),
  });
}
